import React from 'react';
import PropTypes from 'prop-types';
import ModalPortal from '../Modal/Modal';
import { NOOP } from '../../../../utils/const';
import DontShowAgain from '../../../DontShowAgain/DontShowAgain';
import useDontShowAgainMessageRead from '../../../DontShowAgain/useDontShowAgainMessageRead';
import { createClosedByDontShowAgainEvent } from '../Modal/utils';

/**
Modale di conferma. Per avvertire l'utente che quello che sta per fare è irreversibile.

_Quando si utilizza?_

- Usare le confirm solo quando è strettamente necessario e non per azioni di routine.
- Essere molto specifici (vedi sezione Testi) ed informare gli utenti delle conseguenze che stanno per avvenire,
facendo riconoscere il possibile errore in maniera semplice.
- Usare opzioni di risposta (testi dei pulsanti) parlanti, che sintetizzino bene le conseguenze ed evitare risposte Sì/No.
Ad es. in caso di eliminazione di un file scrivere "Elimina il file" "Mantieni il file"
- Essendo che l'utente deve controllare e ricontrollare quello che sta facendo e
che bisogna evitare che compia delle azioni automatiche (come cliccare subito sul pulsante a enfasi alta)
dobbiamo creare delle situazioni diverse da quelle a cui è abituato.
- Prevedere una checkbox "Non mostrare più questo messaggio" nel caso di confirm create per educare l'utente all'utilizzo di nuove
feature del software con effetti indesiderati. In questo caso la confirm sarà mostrata per un periodo di tempo prestabilito.

*/
const ConfirmModal = ({
  show, className, initialPos, ...confirmProps
}) => {
  const messageRead = useDontShowAgainMessageRead(confirmProps.dontShowAgainId, show);
  if (messageRead) {
    confirmProps.onClose(createClosedByDontShowAgainEvent(undefined, confirmProps.dontShowAgainId));
    return null;
  }
  return (
    <ModalPortal show={show} className={className} initialPos={initialPos}>
      <ModalPortal.Confirm id="confirmModal" {...confirmProps} />
    </ModalPortal>
  );
};

ConfirmModal.propTypes = {
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Indica se mostrare o meno la modale */
  show: PropTypes.bool,

  /** Immagine da mostrare per dare enfasi al messaggio. L'immagine deve stare in un quadrato di 120px. */
  image: PropTypes.node,

  /**
Callback di chiusura della modale per la gestione dontShowAgain.
   */
  onClose: PropTypes.func,

  /** Etichetta del bottone di scelta primaria */
  primaryLabel: PropTypes.string.isRequired,
  /**
Callback alla pressione della scelta primaria. Il primo parametro è l'evento scatenante.
   */
  onPrimaryClick: PropTypes.func,

  /** Etichetta del bottone di scelta secondaria */
  secondaryLabel: PropTypes.string.isRequired,
  /**
Callback alla pressione della scelta secondaria. Il primo parametro è l'evento scatenante.
   */
  onSecondaryClick: PropTypes.func,

  /**
Identificativo del componente per la gestione 'Non visualizzare più'. Se impostato abilita la gestione.
Lo stato è salvato nella sessione locale del browser quindi è necessario un identificativo per gestire il salvataggio.

Tipicamente è composto dall'id dell'utente e da un identificativo del messaggio.

Utilizzare `ConfirmModal.clearMessageStatus` per pulire lo stato.
  */
  dontShowAgainId: PropTypes.string,

  /**
Messaggio del componente per la gestione 'Non visualizzare più'.
  */
  dontShowAgainMessage: PropTypes.string,

  /** Titolo */
  title: PropTypes.string.isRequired,
  /** Messaggio o contenuto */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** Indica la posizione iniziale di apertura della modale */
  initialPos: PropTypes.shape({
    x: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    y: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
};

ConfirmModal.defaultProps = {
  className: undefined,
  show: false,
  onPrimaryClick: NOOP,
  onSecondaryClick: NOOP,
  onClose: NOOP,
  dontShowAgainId: undefined,
  dontShowAgainMessage: 'Non mostrare più questo messaggio',
  image: undefined,
  initialPos: undefined,
};

ConfirmModal.clearMessageStatus = DontShowAgain.clearMessageStatus;

export default ConfirmModal;
